import { AppFeatures } from '@/types';
import { AppRoutes, SystemAdministrationRoutes } from '@/routes';
import { FEATURE_FLAG, useFeatureFlag } from '@/feature-flags';
import {
  faBarChart,
  faBarsProgress,
  faBriefcase,
  faBuilding,
  faCalendar,
  faCalendarDays,
  faCarBurst,
  faClipboardCheck,
  faCog,
  faEnvelope,
  faFile,
  faFileContract,
  faFileLines,
  faGaugeHigh,
  faImage,
  faListCheck,
  faLocationDot,
  faMapMarkerAlt,
  faPassport,
  faShieldHalved,
  faShip,
  faTriangleExclamation,
  faTruckFast,
  faUsersGear,
  faUserTag,
  IconDefinition,
  Icons,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { useTheme } from 'styled-components';
import { useAppCustomers } from './useAppCustomers';

export type PageLinkInfo = {
  to: string;
  icon: IconDefinition | Icons;
  color: string;
  label: string;
  subRoutes?: PageLinkInfo[];
  enabled?: boolean;
  hideInMenu?: boolean;
};

const QUESTIONNAIRE_MENU_GROUP = 'questionnaires-group';

export const usePageAccess = () => {
  const theme = useTheme();
  const { t } = useTranslate();
  const commandCentreMenuVisible = useFeatureFlag(FEATURE_FLAG.commandCentre);
  const questionnaireMenuVisible = useFeatureFlag(FEATURE_FLAG.questionnaire);
  const loadPullsMenuVisible = useFeatureFlag(FEATURE_FLAG.loadPulls);
  const {
    hasAdminAccess,
    hasSuperAdminAccess,
    hasLoadLaneOperateAccess,
    hasGbaAccess,
    hasFeatureAccess,
  } = useAppCustomers();

  const gbaAdminAccess = hasGbaAccess && hasAdminAccess;
  const questionnaireResponsesAccess = hasFeatureAccess(
    AppFeatures.QuestionnaireResponses,
  );
  const questionnaireTemplatesAccess = hasFeatureAccess(
    AppFeatures.QuestionnaireTemplates,
  );

  const pageAccess: PageLinkInfo[] = [
    {
      to: AppRoutes.operationsDashboard,
      icon: faGaugeHigh,
      color: theme.defaultIconLinkColour,
      label: t('dashboard.title'),
      enabled: hasSuperAdminAccess,
    },
    {
      to: AppRoutes.commandCentre,
      icon: faMapMarkerAlt,
      color: theme.defaultIconLinkColour,
      label: t('commandCentre'),
      enabled: hasFeatureAccess(AppFeatures.CommandCentre),
      hideInMenu: !commandCentreMenuVisible,
    },
    {
      to: AppRoutes.passport,
      icon: faPassport,
      color: theme.defaultIconLinkColour,
      label: t('vehiclePassport'),
      enabled: hasFeatureAccess(AppFeatures.Passport),
    },
    {
      to: AppRoutes.vessels,
      icon: faShip,
      color: theme.defaultIconLinkColour,
      label: t('vesselsSummary'),
      enabled: hasFeatureAccess(AppFeatures.Vessel),
    },
    {
      to: AppRoutes.storage,
      icon: Icons.compound,
      color: theme.defaultIconLinkColour,
      label: t('compoundStorage'),
      enabled: hasFeatureAccess(AppFeatures.Storage),
    },
    {
      to: AppRoutes.inspections,
      icon: faCarBurst,
      color: theme.defaultIconLinkColour,
      label: t('inspectionSearch'),
      subRoutes: [
        {
          to: AppRoutes.inspection,
          icon: faCarBurst,
          color: theme.defaultIconLinkColour,
          label: t('inspection'),
          enabled: hasFeatureAccess(AppFeatures.Inspection),
          hideInMenu: true,
        },
      ],
      enabled: hasFeatureAccess(AppFeatures.Inspections),
    },
    {
      to: AppRoutes.damages,
      icon: faTriangleExclamation,
      color: theme.defaultIconLinkColour,
      label: t('damageExceptions'),
      enabled: hasFeatureAccess(AppFeatures.Damages),
    },
    {
      to: AppRoutes.jobs,
      icon: faBarsProgress,
      color: theme.defaultIconLinkColour,
      label: t('compoundJobs'),
      enabled: hasFeatureAccess(AppFeatures.Jobs),
    },
    {
      to: AppRoutes.loadlanes,
      icon: faTruckFast,
      color: theme.defaultIconLinkColour,
      label: t('loadLane'),
      enabled: hasLoadLaneOperateAccess, //hasFeatureAccess(AppFeatures.loadlanes),
      hideInMenu: !loadPullsMenuVisible,
    },
    {
      to: AppRoutes.transportSummary,
      icon: Icons.shortTransporter,
      color: theme.defaultIconLinkColour,
      label: t('transportSummary'),
      enabled: hasFeatureAccess(AppFeatures.TransportMovement),
    },
    {
      to: AppRoutes.reports,
      icon: faBarChart,
      color: theme.defaultIconLinkColour,
      label: t('reportsPowerBI'),
      enabled: hasFeatureAccess(AppFeatures.Reports),
    },
    {
      to: QUESTIONNAIRE_MENU_GROUP,
      icon: faClipboardCheck,
      color: theme.defaultIconLinkColour,
      label: t('questionnaires'),
      subRoutes: [
        {
          to: AppRoutes.questionnaireResponses,
          icon: faClipboardCheck,
          color: theme.defaultIconLinkColour,
          label: t('questionnairesMenuResponses'),
          enabled: questionnaireResponsesAccess,
        },
        {
          to: AppRoutes.questionnaireTemplates,
          icon: faFileContract,
          color: theme.defaultIconLinkColour,
          label: t('questionnairesMenuTemplates'),
          enabled: questionnaireTemplatesAccess,
        },
      ],
      enabled: questionnaireResponsesAccess || questionnaireTemplatesAccess,
      hideInMenu: !questionnaireMenuVisible,
    },
    {
      to: AppRoutes.pipeline,
      icon: Icons.compound,
      color: theme.defaultIconLinkColour,
      label: t('pipeline'),
      enabled: false, //hasFeatureAccess(AppFeatures.Pipeline),
      hideInMenu: true,
    },
    {
      to: AppRoutes.vin,
      icon: faMapMarkerAlt,
      color: theme.defaultIconLinkColour,
      label: t('vinTracking'),
      enabled: false, // hasFeatureAccess(AppFeatures.VinTracking),
    },
    {
      to: AppRoutes.pts,
      icon: faBuilding,
      color: theme.defaultIconLinkColour,
      label: t('pts'),
      enabled: false, //hasFeatureAccess(AppFeatures.StockSheet),
    },
    {
      to: AppRoutes.order,
      icon: faFile,
      color: theme.defaultIconLinkColour,
      label: t('orderSummary'),
      enabled: false, //hasFeatureAccess(AppFeatures.Order),
    },
    {
      to: AppRoutes.shippingPlan,
      icon: faCalendar,
      color: theme.defaultIconLinkColour,
      label: t('shippingPlan'),
      enabled: false, //hasFeatureAccess(AppFeatures.ShippingPlan),
    },
    {
      to: AppRoutes.documentManagement,
      icon: faFileLines,
      color: theme.defaultIconLinkColour,
      label: t('documentManagement'),
      enabled: false, //hasFeatureAccess(AppFeatures.DocumentManagement),
    },
    {
      to: AppRoutes.customerManagement,
      icon: faUsersGear,
      color: theme.defaultIconLinkColour,
      label: t('customerManagement'),
      enabled: gbaAdminAccess,
    },
    {
      to: AppRoutes.manageUsers,
      icon: faUserTag,
      color: theme.defaultIconLinkColour,
      label: t('manageUsers'),
      enabled: gbaAdminAccess,
    },
    {
      to: AppRoutes.systemAdministration,
      icon: faCog,
      color: theme.defaultIconLinkColour,
      label: t('systemAdministration'),
      subRoutes: [
        {
          to: SystemAdministrationRoutes.jobs,
          icon: faBarsProgress,
          color: theme.defaultLink,
          label: t('jobs'),
          enabled: gbaAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.locations,
          icon: faLocationDot,
          color: theme.defaultLink,
          label: t('locations'),
          enabled: gbaAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.transportUnits,
          icon: faTruckFast,
          color: theme.defaultLink,
          label: t('transportUnits'),
          enabled: gbaAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.companies,
          icon: faBriefcase,
          color: theme.defaultLink,
          label: t('companies'),
          enabled: gbaAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.shippingPlan,
          icon: faCalendarDays,
          color: theme.defaultLink,
          label: t('shippingPlan'),
          enabled: gbaAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.checklist,
          icon: faListCheck,
          color: theme.defaultLink,
          label: t('checklist'),
          enabled: gbaAdminAccess,
        },
        {
          to: AppRoutes.productImages,
          icon: faImage,
          color: theme.defaultLink,
          label: t('imagery.title'),
          enabled: gbaAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.inspectionStatusAccessRules,
          icon: faShieldHalved,
          color: theme.defaultLink,
          label: t('inspectionStatusAccessRules'),
          enabled: hasSuperAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.emailNotificationAccess,
          icon: faEnvelope,
          color: theme.defaultLink,
          label: t('emailNotificationAccess'),
          enabled: hasSuperAdminAccess,
        },
        {
          to: SystemAdministrationRoutes.stockReconciliation,
          icon: faClipboardCheck,
          color: theme.defaultLink,
          label: t('stockReconciliation'),
          enabled: gbaAdminAccess,
        },
      ],
      enabled: gbaAdminAccess,
    },
    {
      to: AppRoutes.releaseNotes,
      icon: faFileLines,
      color: theme.defaultIconLinkColour,
      label: t('releaseNotes'),
      enabled: true,
    },
  ];
  return pageAccess;
};
